import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  Edit3,
  Trash2,
  Search,
  Wrench,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
} from "lucide-react";
import EquipmentFormModal from "./EquipmentFormModal";
import equipmentService from "@/services/equipmentService";
import { showError, showInfo } from "@/store/errorStore";

/**
 * Componente de gerenciamento de equipamentos
 * CRUD completo com listagem, criação, edição e exclusão
 */
export default function EquipmentsManagement() {
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [savingEquipment, setSavingEquipment] = useState(false);
  const [deletingEquipmentId, setDeletingEquipmentId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  /**
   * Carregar equipamentos
   */
  const loadData = async () => {
    setLoading(true);
    try {
      const data = await equipmentService.getAllEquipments();
      setEquipments(Array.isArray(data) ? data : []);
    } catch (error) {
      // Se for 404, apenas define array vazio (sem mostrar erro)
      if (error.response?.status === 404) {
        setEquipments([]);
      } else {
        // Para outros erros, mostra mensagem
        showError(
          error.response?.data?.message || "Erro ao carregar equipamentos",
          "Erro de Carregamento"
        );
        setEquipments([]);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Buscar todos os equipamentos
   */
  const fetchEquipments = async () => {
    try {
      const data = await equipmentService.getAllEquipments();
      setEquipments(Array.isArray(data) ? data : []);
    } catch (error) {
      // Se for 404, apenas define array vazio (sem mostrar erro)
      if (error.response?.status === 404) {
        setEquipments([]);
      } else {
        // Para outros erros, mostra mensagem
        showError(
          error.response?.data?.message || "Erro ao carregar equipamentos",
          "Erro de Carregamento"
        );
        setEquipments([]);
      }
    }
  };

  /**
   * Abrir modal para criar novo equipamento
   */
  const handleCreateEquipment = () => {
    setEditingEquipment(null);
    setShowModal(true);
  };

  /**
   * Abrir modal para editar equipamento
   */
  const handleEditEquipment = (equipment) => {
    setEditingEquipment(equipment);
    setShowModal(true);
  };

  /**
   * Salvar equipamento (criar ou atualizar)
   */
  const handleSaveEquipment = async (equipmentData) => {
    setSavingEquipment(true);
    try {
      if (editingEquipment) {
        // Atualizar equipamento existente
        await equipmentService.updateEquipment(
          editingEquipment.id,
          equipmentData
        );
        showInfo(
          "Equipamento atualizado com sucesso",
          "Equipamento Atualizado"
        );
      } else {
        // Criar novo equipamento
        await equipmentService.createEquipment(equipmentData);
        showInfo("Equipamento criado com sucesso", "Equipamento Criado");
      }

      setShowModal(false);
      setEditingEquipment(null);
      await fetchEquipments();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Erro ao salvar equipamento";
      showError(errorMessage, "Erro");
    } finally {
      setSavingEquipment(false);
    }
  };

  /**
   * Confirmar exclusão de equipamento
   */
  const handleDeleteClick = (equipment) => {
    setDeletingEquipmentId(equipment.id);
    setShowDeleteDialog(true);
  };

  /**
   * Executar exclusão de equipamento
   */
  const handleConfirmDelete = async () => {
    if (!deletingEquipmentId) return;

    try {
      await equipmentService.deleteEquipment(deletingEquipmentId);
      showInfo("Equipamento removido com sucesso", "Equipamento Deletado");
      await fetchEquipments();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Erro ao deletar equipamento";
      showError(errorMessage, "Erro");
    } finally {
      setShowDeleteDialog(false);
      setDeletingEquipmentId(null);
    }
  };

  /**
   * Filtrar equipamentos por busca
   */
  const filteredEquipments = equipments.filter((equipment) => {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();
    const name = (equipment?.name || "").toLowerCase();
    const brand = (equipment?.brand || "").toLowerCase();
    const model = (equipment?.model || "").toLowerCase();
    const typeLabel = equipment?.type
      ? equipmentService.getEquipmentTypeLabel(equipment.type)
      : "";
    const type = typeLabel.toLowerCase();

    return (
      name.includes(query) ||
      brand.includes(query) ||
      model.includes(query) ||
      type.includes(query)
    );
  });

  /**
   * Calcular estatísticas
   */
  const calculateStats = () => {
    const totalEquipments = equipments.length;
    const activeEquipments = equipments.filter(
      (eq) => eq.status === "ACTIVE"
    ).length;
    const maintenanceEquipments = equipments.filter(
      (eq) => eq.status === "MAINTENANCE"
    ).length;
    const inactiveEquipments = equipments.filter(
      (eq) => eq.status === "INACTIVE"
    ).length;

    // Verificar garantias vencidas ou próximas de vencer
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    const warrantyExpiring = equipments.filter((eq) => {
      if (!eq.warrantyExpiry) return false;
      const warrantyDate = new Date(eq.warrantyExpiry);
      return warrantyDate > today && warrantyDate <= thirtyDaysFromNow;
    }).length;

    return {
      totalEquipments,
      activeEquipments,
      maintenanceEquipments,
      inactiveEquipments,
      warrantyExpiring,
    };
  };

  const stats = calculateStats();

  /**
   * Formatar data para exibição
   */
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  };

  /**
   * Verificar se garantia está próxima de vencer
   */
  const isWarrantyExpiringSoon = (warrantyExpiry) => {
    if (!warrantyExpiry) return false;
    const today = new Date();
    const warrantyDate = new Date(warrantyExpiry);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    return warrantyDate > today && warrantyDate <= thirtyDaysFromNow;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Gestão de Equipamentos
          </h1>
          <p className="text-gray-500 mt-1">
            {filteredEquipments.length}{" "}
            {filteredEquipments.length === 1 ? "equipamento" : "equipamentos"}{" "}
            cadastrados
          </p>
        </div>

        <Button
          onClick={handleCreateEquipment}
          style={{ background: "#c9332C" }}
          onMouseEnter={(e) => (e.target.style.background = "#a02820")}
          onMouseLeave={(e) => (e.target.style.background = "#c9332C")}
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Equipamento
        </Button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">
              Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalEquipments}</div>
            <p className="text-xs text-gray-500 mt-1">Equipamentos</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">
              Ativos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {stats.activeEquipments}
            </div>
            <p className="text-xs text-gray-500 mt-1">Em operação</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">
              Manutenção
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {stats.maintenanceEquipments}
            </div>
            <p className="text-xs text-gray-500 mt-1">Em manutenção</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">
              Inativos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {stats.inactiveEquipments}
            </div>
            <p className="text-xs text-gray-500 mt-1">Desativados</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">
              Garantia
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                stats.warrantyExpiring > 0 ? "text-orange-600" : "text-gray-600"
              }`}
            >
              {stats.warrantyExpiring}
            </div>
            <p className="text-xs text-gray-500 mt-1">Próximo de vencer</p>
          </CardContent>
        </Card>
      </div>

      {/* Busca */}
      <Card>
        <CardHeader>
          <CardTitle>Buscar Equipamentos</CardTitle>
          <CardDescription>
            Pesquise por nome, marca, modelo ou tipo
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Digite para buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Equipamentos */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Equipamentos</CardTitle>
          <CardDescription>
            Gerencie os equipamentos cadastrados no sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-600">Carregando equipamentos...</p>
              </div>
            </div>
          ) : filteredEquipments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Wrench className="w-12 h-12 text-gray-400 mb-3" />
              <p className="text-gray-600 font-medium">
                {searchQuery
                  ? "Nenhum equipamento encontrado"
                  : "Nenhum equipamento cadastrado"}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {searchQuery
                  ? "Tente outro termo de busca"
                  : "Clique em 'Novo Equipamento' para começar"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Marca/Modelo</TableHead>
                    <TableHead>Data Compra</TableHead>
                    <TableHead>Garantia</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEquipments.map((equipment) => {
                    const statusInfo = equipmentService.getStatusInfo(
                      equipment.status
                    );
                    const warrantyExpiring = isWarrantyExpiringSoon(
                      equipment.warrantyExpiry
                    );

                    return (
                      <TableRow key={equipment.id}>
                        <TableCell className="font-medium">
                          {equipment.name}
                        </TableCell>
                        <TableCell>
                          {equipmentService.getEquipmentTypeLabel(
                            equipment.type
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {equipment.brand || "-"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {equipment.model || "-"}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Calendar className="w-3 h-3 text-gray-400" />
                            {formatDate(equipment.purchaseDate)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div
                            className={`flex items-center gap-1 text-sm ${
                              warrantyExpiring ? "text-orange-600" : ""
                            }`}
                          >
                            {warrantyExpiring && (
                              <AlertTriangle className="w-3 h-3" />
                            )}
                            {formatDate(equipment.warrantyExpiry)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`
                              ${
                                statusInfo.color === "green"
                                  ? "bg-green-100 text-green-800 border-green-200"
                                  : ""
                              }
                              ${
                                statusInfo.color === "yellow"
                                  ? "bg-yellow-100 text-yellow-800 border-yellow-200"
                                  : ""
                              }
                              ${
                                statusInfo.color === "red"
                                  ? "bg-red-100 text-red-800 border-red-200"
                                  : ""
                              }
                            `}
                          >
                            {statusInfo.color === "green" && (
                              <CheckCircle className="w-3 h-3 mr-1" />
                            )}
                            {statusInfo.color === "yellow" && (
                              <Clock className="w-3 h-3 mr-1" />
                            )}
                            {statusInfo.color === "red" && (
                              <AlertTriangle className="w-3 h-3 mr-1" />
                            )}
                            {statusInfo.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditEquipment(equipment)}
                            >
                              <Edit3 className="w-4 h-4 mr-1" />
                              Editar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteClick(equipment)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Excluir
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Equipment Form Modal */}
      <EquipmentFormModal
        open={showModal}
        onOpenChange={setShowModal}
        equipment={editingEquipment}
        onSave={handleSaveEquipment}
        loading={savingEquipment}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este equipamento? Esta ação não
              pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingEquipmentId(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
